import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
} from "@altimateai/lego";
import { IconArrowLeft } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { getOwnershipConflicts } from "@/helpers/apis";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { TableHeaderType } from "@/components/tables/types";
import { DatatableContextProvider } from "@/components/tables/contexts/DatatableContextProvider";
import type { OwnershipConflictDetail, OwnershipRequestPayload } from "./types";
import { formatCurrencyAmount } from "./ownershipHelpers";

interface ViewConflictsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownershipRequests: OwnershipRequestPayload[];
  startDate: string;
  endDate: string;
  onValidateForm?: () => Promise<boolean>;
  onBack?: () => void;
}

const ViewConflictsModal = ({
  open,
  onOpenChange,
  ownershipRequests,
  startDate,
  endDate,
  onBack,
}: ViewConflictsModalProps) => {
  const {
    data: conflicts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ownership-conflicts", ownershipRequests, startDate, endDate],
    queryFn: () =>
      getOwnershipConflicts(
        { ownership_requests: ownershipRequests },
        { start_date: startDate, end_date: endDate }
      ),
    enabled: open && ownershipRequests.length > 0,
  });

  const errorMessage =
    error instanceof Error ? error.message : "Failed to load conflict details";

  const tableHeaders = useMemo<TableHeaderType<OwnershipConflictDetail>[]>(() => {
    if (!conflicts) return [];

    return [
      {
        id: 1,
        label: "Team(s)",
        uid: "team_name",
        width: "20%",
        render: (item: OwnershipConflictDetail) => (
          <div className="al-font-medium">{item.team_name}</div>
        ),
      },
      {
        id: 2,
        label: "Conflicting entities",
        uid: "conflicting_entity_count",
        width: "20%",
        render: (item: OwnershipConflictDetail) => (
          <div className="al-text-right al-font-medium">{item.conflicting_entity_count}</div>
        ),
      },
      {
        id: 3,
        label: "Conflicting cost",
        uid: "conflicting_amount",
        width: "20%",
        render: (item: OwnershipConflictDetail) => (
          <div className="al-text-right al-font-medium">
            {formatCurrencyAmount(item.conflicting_amount, conflicts.currency)}
          </div>
        ),
      },
    ];
  }, [conflicts]);

  const tableData = useMemo(() => {
    if (!conflicts) return { items: [] };
    return { items: conflicts.conflicts };
  }, [conflicts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!al-max-w-5xl">
        <Stack direction="column" className="al-h-full">
          <DialogHeader className="al-flex-shrink-0">
            <Stack direction="row" align="center" gap="sm">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (onBack) {
                    onBack();
                  } else {
                    onOpenChange(false);
                  }
                }}
                className="al-h-8 al-w-8 al-p-0"
              >
                <IconArrowLeft size={18} />
              </Button>
              <DialogTitle>View Conflicts</DialogTitle>
            </Stack>
          </DialogHeader>

          <div className="al-flex-1 al-overflow-hidden">
            {isLoading && (
              <Stack justify="center" align="center" className="al-py-12">
                <p className="al-text-muted-foreground">Loading conflicts...</p>
              </Stack>
            )}

            {error && (
              <div className="al-rounded-md al-bg-destructive/10 al-border al-border-destructive/20 al-p-4">
                <p className="al-text-sm al-text-destructive">{errorMessage}</p>
              </div>
            )}

            {!isLoading && !error && conflicts && (
              <>
                {conflicts.conflicts.length === 0 ? (
                  <Stack justify="center" align="center" className="al-py-12">
                    <p className="al-text-muted-foreground">No conflicts found</p>
                  </Stack>
                ) : (
                  <DatatableContextProvider>
                    <div className="al-h-full al-overflow-hidden">
                      <DataTable
                        header={tableHeaders}
                        data={tableData}
                        isLoading={isLoading}
                        minWidth={600}
                      />
                    </div>
                  </DatatableContextProvider>
                )}
              </>
            )}
          </div>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ViewConflictsModal;
